import { useEffect, useState } from "react";
import { Sparkles, Loader2, Target } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell } from "recharts";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";

const tooltipStyle = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 };
const tick = { fontSize: 11, fill: "hsl(var(--muted-foreground))" };
const prColor = { High: "bg-rose-500/15 text-rose-400", Medium: "bg-amber-500/15 text-amber-500", Low: "bg-emerald-500/15 text-emerald-500" };

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [rec, setRec] = useState(null);
  const [loadingRec, setLoadingRec] = useState(false);

  const loadStats = () => {
    setStatsError(null);
    api.get("/analytics/me")
      .then((r) => setStats(r.data))
      .catch((e) => setStatsError(errMsg(e)));
  };

  useEffect(() => {
    loadStats();
  }, []);

  const getRecommendations = async () => {
    setLoadingRec(true);
    try {
      const { data } = await api.post("/analytics/recommend");
      setRec(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoadingRec(false);
    }
  };

  if (statsError) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="analytics-error">
        <div className="max-w-md w-full rounded-3xl border border-border bg-card p-8 text-center">
          <p className="text-sm font-semibold text-destructive mb-2">Couldn't load your progress</p>
          <p className="text-xs text-muted-foreground mb-6">{statsError}</p>
          <Button data-testid="analytics-retry-btn" onClick={loadStats} className="rounded-full glow-btn" size="sm">Retry</Button>
        </div>
      </div>
    );
  }

  if (!stats) return <div className="min-h-[60vh] flex items-center justify-center" data-testid="analytics-loading"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const chartData = stats.score_history.map((s, i) => ({ name: `#${i + 1}`, score: s.percentage }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="analytics-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">My Progress</p>
      <h1 className="text-4xl font-bold tracking-tighter">Revision Analytics</h1>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Papers generated", value: stats.papers_generated },
          { label: "Papers marked", value: stats.papers_completed },
          { label: "Average score", value: `${stats.average_score}%` },
          { label: "Minutes revised", value: stats.time_spent_minutes },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-border bg-card px-5 py-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-3xl font-bold font-display">{s.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {stats.subject_grade && (
        <div className="mt-6 rounded-3xl border border-primary/40 bg-card p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6" data-testid="subject-grade-card">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">A-Level subject grade estimate</p>
            <p className="text-6xl font-bold font-display mt-1" data-testid="subject-grade-value">{stats.subject_grade.grade}</p>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-md leading-snug">
              {stats.subject_grade.raw_mark}/{stats.subject_grade.max_mark} raw marks • {stats.subject_grade.label}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 md:ml-auto">
            {stats.subject_grade.papers.map((p) => (
              <div key={p.paper_number} className="rounded-2xl bg-secondary px-4 py-3 text-center" data-testid={`subject-grade-paper-${p.paper_number}`}>
                <p className="text-xs text-muted-foreground">Paper {p.paper_number}</p>
                <p className="font-semibold font-display">{p.awarded}/{p.available}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-3xl border border-border bg-card p-6">
          <h3 className="font-semibold tracking-tight mb-1">Score History</h3>
          <p className="text-xs text-muted-foreground mb-4">Percentage across marked submissions</p>
          <div className="h-60" data-testid="score-history-chart">
            {chartData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={tick} />
                  <YAxis domain={[0, 100]} tick={tick} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line type="monotone" dataKey="score" stroke="#6366F1" strokeWidth={2.5} dot={{ r: 4, fill: "#6366F1" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground pt-16 text-center">Mark a paper to see your score history.</p>}
          </div>
        </div>

        <div className="rounded-3xl border border-border bg-card p-6">
          <h3 className="font-semibold tracking-tight mb-1">Topic Performance</h3>
          <p className="text-xs text-muted-foreground mb-4">Percentage by topic — red bars need attention</p>
          <div className="h-60" data-testid="topic-performance-chart">
            {stats.topic_breakdown.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.topic_breakdown} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" domain={[0, 100]} tick={tick} />
                  <YAxis type="category" dataKey="topic" width={130} tick={tick} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="percentage" radius={[0, 8, 8, 0]}>
                    {stats.topic_breakdown.map((t, i) => <Cell key={i} fill={t.percentage < 50 ? "#F43F5E" : t.percentage < 70 ? "#F59E0B" : "#10B981"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground pt-16 text-center">No topic data yet — get a paper marked.</p>}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-3xl border border-border bg-card p-6" data-testid="weakest-topics">
          <h3 className="font-semibold tracking-tight mb-4">Weakest Topics</h3>
          {stats.weakest_topics.length ? stats.weakest_topics.map((t) => (
            <div key={t.topic} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <span className="text-sm">{t.topic}</span><Badge className="bg-rose-500/15 text-rose-400 hover:bg-rose-500/15">{t.percentage}%</Badge>
            </div>
          )) : <p className="text-sm text-muted-foreground">Nothing yet.</p>}
        </div>
        <div className="rounded-3xl border border-border bg-card p-6" data-testid="strongest-topics">
          <h3 className="font-semibold tracking-tight mb-4">Strongest Topics</h3>
          {stats.strongest_topics.length ? stats.strongest_topics.map((t) => (
            <div key={t.topic} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <span className="text-sm">{t.topic}</span><Badge className="bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/15">{t.percentage}%</Badge>
            </div>
          )) : <p className="text-sm text-muted-foreground">Nothing yet.</p>}
        </div>
      </div>

      <div className="mt-6 rounded-3xl border border-border bg-card p-6 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
          <h3 className="font-semibold tracking-tight flex items-center gap-2"><Target className="h-4 w-4 text-primary" />What should I revise next?</h3>
          <Button data-testid="get-recommendations-btn" onClick={getRecommendations} disabled={loadingRec} className="rounded-full glow-btn" size="sm">
            {loadingRec ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Thinking…</> : <><Sparkles className="h-4 w-4 mr-2" />Ask the AI Coach</>}
          </Button>
        </div>
        {rec && (
          <div className="mt-4 space-y-3" data-testid="recommendations-list">
            <p className="text-sm"><span className="font-semibold text-primary">Focus topic: </span>{rec.focus_topic}</p>
            {rec.recommendations.map((r, i) => (
              <div key={i} className="rounded-2xl bg-secondary p-4">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <p className="font-medium text-sm">{r.title}</p>
                  <Badge className={`${prColor[r.priority] || prColor.Medium} text-xs`}>{r.priority}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{r.reason}</p>
                <p className="text-xs mt-1.5"><span className="text-primary font-medium">Action: </span>{r.action}</p>
              </div>
            ))}
            <p className="text-xs italic text-muted-foreground">{rec.motivation}</p>
          </div>
        )}
      </div>
    </div>
  );
}
